import pluralize from "pluralize"
import PropTypes from "prop-types"
import React from "react"
import { useFormContext } from "react-hook-form"
import { useMarina } from "src/contexts/MarinaContext"

import Loader from "src/components/Loader"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"
import { parseDateOnly } from "src/utils/date_only"

const formatDay = (date) =>
  date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })

const Label = ({ children }) => (
  <div className="text-xs uppercase tracking-wide text-gray-400">
    {children}
  </div>
)
Label.propTypes = { children: PropTypes.node }

const GenerateInvoiceReviewStep = ({ preview, workOrder, isLoading }) => {
  const { watch } = useFormContext()
  const { presentmentCurrency } = useMarina()

  if (isLoading || !preview) return <Loader name="invoice preview" />

  const dueDate = watch("dueDate")
  const sendEmail = watch("sendEmail")
  const autopayEnabled = watch("autopayEnabled")
  const paymentSummary = autopayEnabled
    ? "AutoPay — card auto-charges on the due date"
    : "Manual collection"
  const money = (amount) =>
    formatCurrencyFromMinorUnits({ amount, currency: presentmentCurrency })

  const { contact, contactBoat, displaySequentialId, marinaToday } = workOrder
  const issuedDate = parseDateOnly(marinaToday)
  const {
    jobs = [],
    subtotal = 0,
    taxAmount = 0,
    total = 0,
    itemizedTaxes = [],
  } = preview

  return (
    <div className="flex flex-col gap-5">
      <div className="overflow-hidden rounded-lg border">
        <div className="flex flex-col gap-4 p-5">
          <div>
            <Label>Invoice preview</Label>
            <div className="text-lg font-bold text-gray-900">Draft</div>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div>
              <Label>Bill to</Label>
              <div className="font-semibold text-gray-900">{contact?.name}</div>
              {contact?.email && (
                <div className="text-sm text-gray-500">{contact.email}</div>
              )}
              {contactBoat?.name && (
                <div className="text-sm text-gray-500">{contactBoat.name}</div>
              )}
              <div className="text-sm text-gray-400">{displaySequentialId}</div>
            </div>
            <div>
              <Label>Issued</Label>
              <div className="text-sm text-gray-900">
                {issuedDate ? formatDay(issuedDate) : "—"}
              </div>
            </div>
            <div>
              <Label>Due</Label>
              <div className="text-sm text-gray-900">
                {dueDate ? formatDay(dueDate) : "—"}
              </div>
            </div>
            <div>
              <Label>Payment</Label>
              <div className="text-sm text-gray-900">{paymentSummary}</div>
            </div>
          </div>
        </div>

        {jobs.map((job) => (
          <div key={job.id}>
            <div className="border-l-4 border-blue-600 bg-gray-100 px-5 py-2">
              <span className="font-semibold text-gray-900">{job.name}</span>
              {job.description && (
                <div className="mt-0.5 border-l-2 border-gray-300 pl-2.5 text-sm italic text-gray-600">
                  {job.description}
                </div>
              )}
            </div>
            <div className="px-5">
              {job.lineItems.map((lineItem) => (
                <div
                  key={lineItem.id}
                  className="flex items-start justify-between gap-4 py-2 text-sm"
                >
                  <div className="flex-1">
                    <div className="text-gray-700">{lineItem.name}</div>
                    {lineItem.notes && (
                      <div className="mt-1 border-l-2 border-gray-300 pl-2.5 italic text-gray-600">
                        {lineItem.notes}
                      </div>
                    )}
                  </div>
                  <span className="text-gray-400">
                    {lineItem.quantity} × {money(lineItem.pricePerUnit)}
                  </span>
                  <span className="w-24 text-right font-semibold text-gray-900">
                    {money(lineItem.subtotal)}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between border-t py-2">
                <Label>Job subtotal</Label>
                <span className="font-bold text-gray-900">
                  {money(job.subtotal)}
                </span>
              </div>
            </div>
          </div>
        ))}

        <div className="flex justify-end bg-gray-100 px-5 py-4">
          <div className="flex w-full max-w-xs flex-col gap-1 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>
                Subtotal ({jobs.length} {pluralize("job", jobs.length)})
              </span>
              <span>{money(subtotal)}</span>
            </div>
            {itemizedTaxes.length > 0 ? (
              itemizedTaxes.map((tax) => (
                <div
                  key={`${tax.name}-${tax.rateDisplay}`}
                  className="flex justify-between text-gray-600"
                >
                  <span>
                    {tax.name} ({tax.rateDisplay})
                  </span>
                  <span>{money(tax.amount)}</span>
                </div>
              ))
            ) : (
              <div className="flex justify-between text-gray-600">
                <span>Tax</span>
                <span>{money(taxAmount)}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-bold text-gray-900">
              <span>Total due</span>
              <span>{money(total)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="flex items-start gap-3 rounded-lg border p-3">
          <i className="icon icon-sf-check text-green-600 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-gray-900">Jobs</div>
            <div className="text-gray-500">
              {jobs.length} {pluralize("job", jobs.length)} will transition to
              Invoiced
            </div>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-lg border p-3">
          <i className="icon icon-md-mail mt-0.5 text-gray-500" />
          <div className="text-sm">
            <div className="font-semibold text-gray-900">Email</div>
            <div className="text-gray-500">
              {!sendEmail
                ? "Not emailed"
                : contact?.email
                ? `Sent to ${contact.email}`
                : "No email on file"}
            </div>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-lg border p-3">
          <i className="icon icon-pos-cash mt-0.5 text-gray-500" />
          <div className="text-sm">
            <div className="font-semibold text-gray-900">Payment</div>
            <div className="text-gray-500">{paymentSummary}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

GenerateInvoiceReviewStep.propTypes = {
  isLoading: PropTypes.bool,
  preview: PropTypes.shape({
    subtotal: PropTypes.number,
    taxAmount: PropTypes.number,
    total: PropTypes.number,
    itemizedTaxes: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        rateDisplay: PropTypes.string,
        amount: PropTypes.number,
      })
    ),
    jobs: PropTypes.array,
  }),
  workOrder: PropTypes.shape({
    marinaToday: PropTypes.string,
    displaySequentialId: PropTypes.string,
    contact: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
    }),
    contactBoat: PropTypes.shape({ name: PropTypes.string }),
  }).isRequired,
}

export default GenerateInvoiceReviewStep
